import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CAMPAIGN_STATUS_LABELS, CAMPAIGN_STATUSES, type CampaignStatus } from '@epasu/core/constants';
import { ArrowLeft, Clock, Pencil, Send } from 'lucide-react';
import type { Audience } from '@epasu/core';
import { crm, type CampaignRow, type LeadListRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select } from '@/components/ui/input';
import { CampaignDialog } from '@/components/CampaignDialog';
import { zeitplanInWorten } from '@/lib/campaign-schedule';
import { WavePreviewDialog } from '@/components/WavePreviewDialog';
import { cn } from '@/lib/utils';
import { CampaignMembersTab } from './CampaignMembersTab';
import { CampaignStepsTab } from './CampaignStepsTab';

type Reiter = 'mitglieder' | 'schritte';

/**
 * Die Kampagnen-Akte: Kennzahlen, Statuswechsel, der Knopf zur nächsten
 * Welle, und die beiden Reiter Mitglieder und Schritte.
 */
export function CampaignDetailPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const [kampagne, setKampagne] = useState<CampaignRow | null | undefined>(undefined);
  const [tab, setTab] = useState<Reiter>('mitglieder');
  const [welle, setWelle] = useState(false);
  const [bearbeiten, setBearbeiten] = useState(false);
  const [statusHinweis, setStatusHinweis] = useState<string | null>(null);
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [ohneVorlage, setOhneVorlage] = useState(false);
  const [listen, setListen] = useState<LeadListRow[]>([]);
  // Nur fuer „Vielleicht geöffnet“ — diese Zahl steht nicht in der Kampagnenzeile.
  const [kennzahlen, setKennzahlen] = useState<Awaited<
    ReturnType<typeof crm.campaigns.metrics>
  > | null>(null);

  const laden = useCallback(() => {
    if (!campaignId) return;
    // Erst leeren: beim Wechsel der Kampagne stuende sonst bis zur Antwort
    // noch die Zahl der vorigen da.
    setKennzahlen(null);
    void crm.campaigns.get(campaignId).then(setKampagne);
    void crm.campaigns.metrics(campaignId).then(setKennzahlen);
    // Ob die Erstansprache eine Vorlage hat, entscheidet, ob ueberhaupt etwas
    // rausgeht — das gehoert neben den Zeitplan und nicht nur in den Reiter,
    // den man dafuer erst oeffnen muesste.
    void crm.campaigns
      .steps(campaignId)
      .then((rows) => setOhneVorlage(rows.find((r) => r.stepNumber === 1)?.templateId == null));
  }, [campaignId]);
  useEffect(laden, [laden]);

  // Fuer den Bearbeiten-Dialog und die Herkunftszeile im Kopf. Der Name der
  // Quell-Liste steht nicht in `CampaignWithProgress` — nur ihre ID.
  const ladeStammdaten = useCallback(() => {
    void crm.audiences.list().then(setAudiences);
    void crm.leadLists.list({ includeArchived: true }).then(setListen);
  }, []);
  useEffect(ladeStammdaten, [ladeStammdaten]);

  if (!campaignId || kampagne === null) {
    return (
      <Card className="space-y-3 p-8 text-center">
        <p className="text-sm text-muted-foreground">Diese Kampagne gibt es nicht (mehr).</p>
        <Link to="/akquise?reiter=kampagnen" className="text-sm text-brand hover:underline">
          Zurück zur Übersicht
        </Link>
      </Card>
    );
  }

  if (kampagne === undefined) {
    return <p className="text-sm text-muted-foreground">Lade …</p>;
  }

  const statusSetzen = async (status: CampaignStatus) => {
    setStatusHinweis(null);
    const ergebnis = await crm.campaigns.setStatus(campaignId, status);
    if (!ergebnis.updated) {
      setStatusHinweis(
        'Es ist schon Post aus dieser Kampagne raus — zurück auf „Entwurf“ geht danach nicht mehr.',
      );
      return;
    }
    laden();
  };

  const antwortquote =
    kampagne.sentCount > 0 ? Math.round((kampagne.repliedCount / kampagne.sentCount) * 100) : null;
  // Dieselbe Rechnung wie die Antwortquote daneben und wie in der Liste —
  // zwei Wege zur selben Zahl liefen auseinander.
  const oeffnungsquote =
    kampagne.sentCount > 0 ? Math.round((kampagne.openedCount / kampagne.sentCount) * 100) : null;
  const quellListe = listen.find((l) => l.id === kampagne.sourceListId);
  const zeitplan = zeitplanInWorten(kampagne);

  return (
    <div className="space-y-4">
      <Link
        to="/akquise?reiter=kampagnen"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Alle Kampagnen
      </Link>

      <Card className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-xl font-semibold">{kampagne.name}</h1>
            <p className="text-sm text-muted-foreground">
              {kampagne.audienceName || '—'} · Tageslimit {kampagne.dailyLimit} · Quell-Liste{' '}
              {quellListe?.name ?? 'keine'}
            </p>
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              {zeitplan ? (
                <>
                  {zeitplan}
                  {/* JSX schluckt den Zeilenumbruch — ohne das Leerzeichen
                      klebte der Zusatz am Wochentag. */}
                  {kampagne.status !== 'aktiv' && ' '}
                  {kampagne.status !== 'aktiv' && (
                    <span className="text-warning">
                      — läuft erst, wenn die Kampagne auf „Aktiv“ steht
                    </span>
                  )}
                </>
              ) : (
                'Kein Zeitplan — es versendet nur „Nächste Welle freigeben“.'
              )}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setBearbeiten(true)}>
              <Pencil className="mr-2 h-4 w-4" />
              Bearbeiten
            </Button>
            <Select
              className="h-9 w-auto"
              value={kampagne.status}
              onChange={(e) => void statusSetzen(e.target.value as CampaignStatus)}
              aria-label="Status"
            >
              {CAMPAIGN_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {CAMPAIGN_STATUS_LABELS[s]}
                </option>
              ))}
            </Select>
            <Button onClick={() => setWelle(true)}>
              <Send className="mr-2 h-4 w-4" />
              Nächste Welle freigeben
            </Button>
          </div>
        </div>

        {ohneVorlage && (
          <p className="mt-3 rounded-md bg-secondary p-3 text-sm text-muted-foreground">
            Der Erstansprache fehlt die Vorlage — diese Kampagne verschickt nichts, auch
            nicht nach Zeitplan. Im Reiter{' '}
            <button
              type="button"
              className="font-medium text-brand hover:underline"
              onClick={() => setTab('schritte')}
            >
              Schritte
            </button>{' '}
            lässt sie sich verfassen.
          </p>
        )}

        {statusHinweis && (
          <p className="mt-3 rounded-md bg-secondary p-3 text-sm text-muted-foreground">
            {statusHinweis}
          </p>
        )}

        <dl
          className={cn(
            'mt-6 grid gap-4',
            kampagne.trackOpens ? 'sm:grid-cols-3 lg:grid-cols-6' : 'sm:grid-cols-4',
          )}
        >
          <Kennzahl label="Mitglieder">{kampagne.memberCount}</Kennzahl>
          <Kennzahl label="Versendet">{kampagne.sentCount}</Kennzahl>
          <Kennzahl label="Geantwortet">{kampagne.repliedCount}</Kennzahl>
          <Kennzahl label="Antwortquote">{antwortquote === null ? '—' : `${antwortquote} %`}</Kennzahl>
          {kampagne.trackOpens && (
            <>
              <Kennzahl label="Geöffnet">
                {kampagne.openedCount}
                {oeffnungsquote !== null && (
                  <span className="ml-1 text-sm font-normal text-muted-foreground">
                    ({oeffnungsquote} %)
                  </span>
                )}
              </Kennzahl>
              <Kennzahl label="Vielleicht geöffnet">
                {kennzahlen ? kennzahlen.contactsMaybeOpened : '—'}
              </Kennzahl>
            </>
          )}
        </dl>
      </Card>

      <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
        {(
          [
            { key: 'mitglieder', label: 'Mitglieder' },
            { key: 'schritte', label: 'Schritte' },
          ] as { key: Reiter; label: string }[]
        ).map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => setTab(r.key)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm transition-colors',
              tab === r.key ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
            )}
          >
            {r.label}
          </button>
        ))}
      </div>

      {tab === 'mitglieder' && (
        // Der `key` setzt den Reiter beim Wechsel der Quell-Liste neu auf: der
        // Stand „so viele warten noch“ stammt aus der alten Liste und waere
        // sonst still falsch.
        <CampaignMembersTab
          key={kampagne.sourceListId ?? 'keine'}
          campaignId={campaignId}
          audienceId={kampagne.audienceId}
          onChanged={laden}
        />
      )}
      {tab === 'schritte' && <CampaignStepsTab campaignId={campaignId} onChanged={laden} />}

      {bearbeiten && (
        <CampaignDialog
          audiences={audiences}
          kampagne={kampagne}
          onClose={() => setBearbeiten(false)}
          onSaved={() => {
            setBearbeiten(false);
            laden();
            // Eine im Dialog gewaehlte Liste kann seit dem letzten Laden neu
            // sein — sonst bliebe die Herkunftszeile im Kopf leer.
            ladeStammdaten();
          }}
        />
      )}

      {welle && (
        <WavePreviewDialog
          campaignId={campaignId}
          onClose={() => setWelle(false)}
          onSent={laden}
        />
      )}
    </div>
  );
}

function Kennzahl({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-lg font-semibold">{children}</dd>
    </div>
  );
}
